import {
  UserType,
  StruggleJounralEntryData,
  ComprehensionJournalEntryData,
  IdpType,
} from './DataProtocolTypes';
import { Mood, SecondaryMood } from './Mood';
import {
  ElementaryUnitName,
  LessonLanguage,
  QuestName,
  CreateAssignmentsRequestBase,
} from './LessonPlanTypes';

export const EventSource = {
  PEEKAPAK: 'peekapak',
  MYPEEKAVILLE: 'mypeekaville',
} as const;

export type EventSource = (typeof EventSource)[keyof typeof EventSource];

export const EventName = {
  INTERACTION_END: 'interaction-end',
  INTERACTION_START: 'interaction-start',
  JOURNAL_ASSIGNMENT_COMPLETE: 'journal-assignment-complete',
  JOURNAL_ASSIGNMENT_OPEN: 'journal-assignment-open',
  JOURNAL_ASSIGNMENT_STORY_OPEN: 'journal-assignment-story-open',
  JOURNAL_BOOK_CLOSE: 'journal-book-close',
  JOURNAL_BOOK_COMPLETE: 'journal-book-complete',
  JOURNAL_BOOK_OPEN: 'journal-book-open',
  JOURNAL_GRADED_MINI_GAME_COMPLETE: 'journal-graded-mini-game-complete',
  JOURNAL_MOOD_LOG: 'journal-mood-log',
  JOURNAL_QUEST_COMPLETE: 'journal-quest-complete',
  JOURNAL_READING_COMPREHENSION_COMPLETE: 'journal-reading-comprehension-complete',
  JOURNAL_STORY_OPEN: 'journal-assignment-story-open',
  JOURNAL_SELECT_INTERESTS: 'journal-select-interests',
  JOURNAL_BREATHING_EXERCISE_START: 'journal-breathing-exercise-start',
  JOURNAL_BREATHING_EXERCISE_COMPLETE: 'journal-breathing-exercise-complete',
  LESSONS_ASSIGNMENTS_CREATE: 'lessons-assignments-create',
  LESSONS_COMPLETE: 'lessons-complete',
  LESSONS_OPEN: 'lessons-open',
  LESSONS_STORY_OPEN: 'lessons-story-open',
  LOGIN: 'login',
  PAGE_VIEW: 'page-view',
  PARENTS_INVITATION_EMAIL_OPEN: 'parents-invitation-email-open',
  PARENTS_RESOURCE_OPEN: 'parents-resource-open',
  INTERNAL_SCENE_LOAD: 'internal-scene-load',
  INTERNAL_BOOK_LOAD: 'internal-book-load',
  INTERNAL_MEMORY_SIZE_SNAPSHOT: 'internal-memory-size-snapshot',
  INTERNAL_CONTROL_CLICK: 'internal-control-click',
  TELEMETRY: 'telemetry',
  LIBRARY_VIEWED: 'library-viewed',
  LIBRARY_SWITCHED: 'library-switched',
  LIBRARY_ACCESS_BLOCKED: 'library-access-blocked',
  CONCERNING_EMOTIONS_EMAIL_SENT: 'concerning-emotions-email-sent',
  CONCERNING_EMOTIONS_EMAIL_OPEN: 'concerning-emotions-email-open',
  CONCERNING_EMOTIONS_EMAIL_CLICK: 'concerning-emotions-email-click',
} as const;

export type EventName = (typeof EventName)[keyof typeof EventName];

export const InteractionEndTrigger = {
  // user has not interacted with the page for a while
  IDLE: 'idle',
  // web browser user unloads page
  UNLOAD: 'unload',
  // mobile application switched to background
  PAUSE: 'pause',
} as const;

export type InteractionEndTrigger =
  (typeof InteractionEndTrigger)[keyof typeof InteractionEndTrigger];

export interface AnalyticsEvent {
  source: EventSource;
  name: EventName;
}

export interface UnauthenticatedEvent extends AnalyticsEvent {
  description?: string;
}

export interface AuthenticatedEvent extends AnalyticsEvent {
  userId: string;
  userType: UserType;
  schoolId: string | 'none';
  district: string | 'none';
  account: string | 'none';
  seller: string | 'none';
  licenseLevel: string;
  licenseExpires: number;
  commitHash?: string; // Frontend git commit hash from build time (not available in server-side events)
}

export type StudentEvent = AuthenticatedEvent & StudentTags;
export type NonStudentEvent = AuthenticatedEvent & NonStudentTags;
export type UserTypeEvent = StudentEvent | NonStudentEvent;
export type AnyUserEvent = UnauthenticatedEvent | UserTypeEvent;

export interface StudentTags {
  userType: typeof UserType.STUDENT;
  grade: number;
  readingLevel: number;
  classroomId: string;
}

export interface NonStudentTags {
  userType: Exclude<UserType, typeof UserType.STUDENT>;
}

/////////////////////////////////////////////////////////////////
//
// user type-specific events
//
/////////////////////////////////////////////////////////////////
export type LoginEvent<T extends StudentEvent | NonStudentEvent> = T & {
  name: typeof EventName.LOGIN;
  loginMethod: IdpType;
};

export type StorybookEvent<T extends UserTypeEvent> = T & {
  unit: ElementaryUnitName;
  readingLevel: number;
  language: LessonLanguage;
};

export type StorybookOpenEvent<T extends UserTypeEvent> = StorybookEvent<T> & {
  name: typeof EventName.JOURNAL_BOOK_OPEN;
};

export type StorybookCloseEvent<T extends UserTypeEvent> = StorybookEvent<T> & {
  name: typeof EventName.JOURNAL_BOOK_CLOSE;
  duration: number; // in seconds
};

export type StorybookCompleteEvent<T extends UserTypeEvent> = StorybookEvent<T> & {
  name: typeof EventName.JOURNAL_BOOK_COMPLETE;
};

export type JournalQuestCompleteEvent<T extends UserTypeEvent> = T & {
  name: typeof EventName.JOURNAL_QUEST_COMPLETE;
  quest: QuestName;
};

export type PageViewEvent = AnyUserEvent & {
  name: typeof EventName.PAGE_VIEW;
  url: string;
};

export type LessonsOpenedEvent = NonStudentEvent & {
  name: typeof EventName.LESSONS_OPEN;
  unit: string;
  lesson: string;
};

export type LessonsStoryOpenEvent = NonStudentEvent & {
  name: typeof EventName.LESSONS_STORY_OPEN;
  story: string;
};

export type JournalAssignmentOpenEvent = StudentEvent & {
  name: typeof EventName.JOURNAL_ASSIGNMENT_OPEN;
  assignmentName: string;
  createdAt: number;
};

export type JournalAssignmentStoryOpenEvent = StudentEvent & {
  name: typeof EventName.JOURNAL_STORY_OPEN;
  story: string;
};

export type JournalGradedMiniGameCompleteEvent = StudentEvent &
  StruggleJounralEntryData & {
    name: typeof EventName.JOURNAL_GRADED_MINI_GAME_COMPLETE;
  };

export type InteractionStartEvent = AnyUserEvent & {
  name: typeof EventName.INTERACTION_START;
};

export type InteractionEndEvent = AnyUserEvent & {
  name: typeof EventName.INTERACTION_END;
  trigger: InteractionEndTrigger;
  duration: number; // in seconds
};

export type JournalMoodLogEvent<T extends UserTypeEvent> = T & {
  name: typeof EventName.JOURNAL_MOOD_LOG;
  mood: Mood | SecondaryMood;
  reasons?: string;
  teacherRequested?: boolean;
  journalEntryCreated?: boolean;
};

export type JournalStruggleEvent<T extends UserTypeEvent> = T &
  StruggleJounralEntryData & {
    name: typeof EventName.JOURNAL_GRADED_MINI_GAME_COMPLETE;
  };

export type JournalReadingComprehensionCompleteEvent<T extends UserTypeEvent> = T &
  Omit<ComprehensionJournalEntryData, 'time' | 'individualQuestionsData'> & {
    name: typeof EventName.JOURNAL_READING_COMPREHENSION_COMPLETE;
    duration: number; // in seconds
  };

export type JournalAssignmentCompleteEvent<T extends StudentEvent> = T & {
  name: typeof EventName.JOURNAL_ASSIGNMENT_COMPLETE;
  unit: string;
  lesson: string;
  module: string;
  metawork?: {
    [k: string]: unknown;
  };
};

export type LessonsAssignmentsCreateEvent<T extends NonStudentEvent> = T &
  CreateAssignmentsRequestBase & {
    name: typeof EventName.LESSONS_ASSIGNMENTS_CREATE;
  };

export type LessonsCompleteEvent<T extends NonStudentEvent> = T & {
  name: typeof EventName.LESSONS_COMPLETE;
  classroomId: string;
  unit: string; // clean unit name only, no curriculum type or grade (PE-1611)
  unitId: string; // raw compound unit id (e.g. "belonging-standard") - kept so existing reports/filters built on the old `unit` values keep working (PE-1611)
  lesson: string; // lesson identifier (parseable: "subunit-0-lesson-1" or lessonId)
  lessonTitle: string; // human-readable lesson title (resolved from titleMap at log time)
  module?: string; // module or step identifier (parseable: "step-2" or moduleId)
  completionType: 'step' | 'lesson' | 'module';
  curriculum: 'elementary' | 'secondary';
  curriculumType: 'MENA' | 'Standard' | 'Modified' | 'Takween'; // Mixpanel breakdown field (PE-1611)
  grade?: number;
  language?: string;
  // NEW: Version and structured position for elementary
  titleMapVersion?: string; // Which title map version was used (elementary only)
  unitPosition?: {
    // Structured position for elementary (for better querying and title resolution)
    subunit: number;
    lesson: number;
    step: number;
  };
};

export type InternalSceneLoadEvent = AnyUserEvent & {
  name: typeof EventName.INTERNAL_SCENE_LOAD;
  sceneName: string;
  duration: number; // seconds
};

export type InternalBookLoadEvent = AnyUserEvent & {
  name: typeof EventName.INTERNAL_BOOK_LOAD;
  unit: ElementaryUnitName;
  readingLevel: number;
  language: LessonLanguage;
  duration: number; // seconds
};

export type InternalMemorySizeSnapshotEvent = AnyUserEvent & {
  name: typeof EventName.INTERNAL_MEMORY_SIZE_SNAPSHOT;
  size: number; // megabytes
};

export type InternalControlClickEvent = AnyUserEvent & {
  name: typeof EventName.INTERNAL_CONTROL_CLICK;
  control: string; // whatever identifi  name: typeof EventName.INTERNAL_CLICK;
  controlType?:
    | 'button'
    | 'link'
    | 'icon'
    | 'menu-item'
    | 'checkbox'
    | 'radio'
    | 'toggle'
    | 'other';
  scene?: string;
  section?: string;
  action?: string;
  label?: string;
  metadata?: Record<string, unknown>;
};

/**
 * Generic telemetry event for flexible debugging and monitoring
 * Allows logging arbitrary structured data without requiring type changes
 *
 * DATA CHUNKING FOR MIXPANEL 255-BYTE LIMIT:
 * Mixpanel has a 255-byte limit per string property. To accommodate larger
 * telemetry payloads, data is split across up to 5 optional fields (telemetryData0-4).
 * Each field can contain up to 255 bytes of JSON stringified data.
 *
 * USAGE:
 * - telemetryName: Identifies the type of telemetry (e.g., 'surveyPromptDecision')
 * - telemetryData0-4: JSON stringified chunks of data (all optional)
 *
 * CHUNK ALLOCATION FOR 'surveyPromptDecision':
 * - telemetryData0: Critical decision (shouldPrompt, reason, decidedBy, hasBackendEligibilityFlag)
 * - telemetryData1: User & campaign context (userId, campaignId, hasUserMadeRecentChoice, currentPath)
 * - telemetryData2: Flags & choice details (hasSurveyParameters, choiceData)
 * - telemetryData3: Reserved for future use
 * - telemetryData4: Reserved for future use
 *
 * See SurveyPromptDecisionData in Frontend/src/SurveyPromptDecisionService.ts for complete structure.
 *
 * Usage example:
 * {
 *   name: 'telemetry',
 *   telemetryName: 'surveyPromptDecision',
 *   telemetryData0: '{"shouldPrompt":false,"reason":"...","decidedBy":"...","hasBackendEligibilityFlag":true}',
 *   telemetryData1: '{"userId":"...","campaignId":"...","hasUserMadeRecentChoice":true,"currentPath":"/"}',
 *   telemetryData2: '{"hasSurveyParameters":true,"choiceData":{...}}',
 *   ...common user properties
 * }
 */
export type TelemetryEvent = AnyUserEvent & {
  name: typeof EventName.TELEMETRY;
  telemetryName: string;
  telemetryData0?: string; // Primary data chunk (up to 255 bytes)
  telemetryData1?: string; // Secondary data chunk (up to 255 bytes)
  telemetryData2?: string; // Tertiary data chunk (up to 255 bytes)
  telemetryData3?: string; // Reserved for future use (up to 255 bytes)
  telemetryData4?: string; // Reserved for future use (up to 255 bytes)
};

export type LibraryName = 'peekapak' | 'takween';
export type ClassroomType = 'EL' | 'MS' | 'HS';

export type LibraryViewedEvent<T extends UserTypeEvent> = T & {
  name: typeof EventName.LIBRARY_VIEWED;
  libraryName: LibraryName;
  classroomType: ClassroomType;
  peekapakAccessStatus: boolean;
};

export type LibrarySwitchedEvent<T extends UserTypeEvent> = T & {
  name: typeof EventName.LIBRARY_SWITCHED;
  fromLibrary: LibraryName;
  toLibrary: LibraryName;
  classroomType: ClassroomType;
};

export type LibraryAccessBlockedEvent<T extends UserTypeEvent> = T & {
  name: typeof EventName.LIBRARY_ACCESS_BLOCKED;
  requestedLibrary: LibraryName;
  classroomType: ClassroomType;
  schoolAccessStatus: boolean;
};

export interface LogEventRequest {
  event: AnyUserEvent;
}
